import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import { Save, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function UsingTheCalendarPage() {
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/calendar-howto")
      .then(({ data }) => {
        if (cancelled) return;
        setContent(data.content || "");
        setOriginal(data.content || "");
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/calendar-howto", { content });
      setOriginal(data.content || "");
      setEditing(false);
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setContent(original);
    setEditing(false);
  };

  return (
    <div className="space-y-6" data-testid="using-the-calendar-page">
      <div className="flex items-start justify-between gap-4">
        <SectionTitle overline="How-to" title="Using the Calendar" />
        {!editing && !loading ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            data-testid="edit-howto-button"
            className="mt-2 shrink-0"
          >
            <Pencil size={14} className="mr-1.5" /> Edit
          </Button>
        ) : null}
      </div>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : editing ? (
            <div className="space-y-3" data-testid="howto-edit-mode">
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={14}
                placeholder="Write your own how-to text here…"
                className="font-sans leading-relaxed"
                data-testid="howto-textarea"
              />
              <div className="flex items-center gap-2">
                <Button
                  onClick={save}
                  disabled={saving}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="howto-save-button"
                >
                  <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={cancel}
                  disabled={saving}
                  data-testid="howto-cancel-button"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div data-testid="howto-view-mode">
              {content.trim() ? (
                <div className="whitespace-pre-wrap text-sm sm:text-base leading-relaxed text-foreground/90">
                  {content}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No how-to yet. Click <strong>Edit</strong> above to write your own.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
